import { z } from "zod";

import { ALERT_MAX_LENGTH, PRIORITIES, TASK_URGENCIES } from "@handoverpro/shared";

/**
 * The patient form's validation schema (E4). This is the *single* schema the
 * `<PatientForm>` resolves against, and it grows as the SBAR (E4.2), Tasks
 * (E4.3), Alert (E4.4) and Priority (E4.5) sections land. E4.1 ships the
 * basic-details fields with their max-length rules; the remaining fields are
 * present (so a submitted payload is complete and edit-mode round-trips
 * losslessly) but only leniently validated until their sections add the UI and
 * tighten the rules.
 *
 * Max-lengths are a client-side affordance specific to this form — the API's
 * DTOs (HAN-46) enforce non-emptiness but not length, so these constants live
 * here rather than in the shared schema.
 */
export const HOSPITAL_NUMBER_MAX = 20;
export const FULL_NAME_MAX = 100;
export const BED_NUMBER_MAX = 20;
export const DIAGNOSIS_MAX = 500;
/** Per-field cap on each SBAR textarea (E4.2). */
export const SBAR_MAX = 2000;
/** Show a SBAR character counter once the value is within this many of the cap. */
export const SBAR_COUNTER_THRESHOLD = 100;
/** Per-row cap on each task description (E4.3). */
export const TASK_DESCRIPTION_MAX = 300;
/** Show the Alert character counter once the value is within this many of the cap (E4.4). */
export const ALERT_COUNTER_THRESHOLD = 20;

const cappedRequiredText = (max: number): z.ZodString =>
  z.string().trim().min(1, "This field is required").max(max, `Must be at most ${max} characters`);

/** Returns today's date as YYYY-MM-DD in the local timezone (for the "past" check). */
function todayIsoDate(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export const patientFormSchema = z.object({
  // --- Basic details (E4.1) ---
  hospitalNumber: cappedRequiredText(HOSPITAL_NUMBER_MAX),
  fullName: cappedRequiredText(FULL_NAME_MAX),
  dateOfBirth: z
    .string()
    .min(1, "This field is required")
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Enter a valid date")
    .refine((value) => value < todayIsoDate(), "Date of birth must be in the past"),
  bedNumber: cappedRequiredText(BED_NUMBER_MAX),
  diagnosis: cappedRequiredText(DIAGNOSIS_MAX),
  // Optional "assign to" — "" means unassigned (mapped to undefined on submit).
  assignedDoctorId: z.string(),

  // --- Carried fields, tightened by follow-up tickets ---
  // Priority dropdown defaults to High (E4.5); the value is already carried so
  // the create payload is valid and the DB default is mirrored client-side.
  priority: z.enum(PRIORITIES),
  // SBAR is mandatory (E4.2): every field is required and non-empty after trim,
  // mirroring `sbarSchema` in @handoverpro/shared, plus a client-only length cap.
  sbar: z.object({
    situation: cappedRequiredText(SBAR_MAX),
    background: cappedRequiredText(SBAR_MAX),
    assessment: cappedRequiredText(SBAR_MAX),
    recommendation: cappedRequiredText(SBAR_MAX),
  }),
  // Task rows (E4.3): each description is required and capped. The cross-field
  // "a High-priority patient needs at least one task" rule is enforced live in
  // the form (the disabled-Save gate) and authoritatively by the API + shared
  // schema, so it isn't duplicated as a resolver refinement here.
  tasks: z.array(
    z.object({
      description: cappedRequiredText(TASK_DESCRIPTION_MAX),
      urgency: z.enum(TASK_URGENCIES),
    }),
  ),
  // The optional one-line clinical alert input arrives in E4.4; cap matches the
  // shared rule so the two can't drift.
  alert: z.string().trim().max(ALERT_MAX_LENGTH, `Must be at most ${ALERT_MAX_LENGTH} characters`),
});

export type PatientFormValues = z.infer<typeof patientFormSchema>;

/** Defaults for an empty (create) form. Priority starts High (E4.5 safety default). */
export const EMPTY_PATIENT_FORM: PatientFormValues = {
  hospitalNumber: "",
  fullName: "",
  dateOfBirth: "",
  bedNumber: "",
  diagnosis: "",
  assignedDoctorId: "",
  priority: "high",
  sbar: { situation: "", background: "", assessment: "", recommendation: "" },
  tasks: [],
  alert: "",
};
